import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import classnames from "classnames";

import styles from "./high-frequency-word.module.css";
import * as highFrequencyWordActions from "../../redux/actions/highFrequencyWord";
import { State } from "../../redux/store";
import { useImgLoaded, useAudio } from "../../hooks/CustomHooks";
import { srcPath } from "../../util/Path";
import WordCard from "./WordCard";
import { maxLeft, defaultWidth, defaultHeight } from "../../util/Constants";
import { backToWeb } from "../../util/Util";
import { TRUCK_SPEED } from "../../redux/actions/highFrequencyWord";

const getTruckSpec = (truckType: number) => {
  switch (truckType) {
    case 1:
      return { truckWidth: 1263, truckInnerWidth: 1010, truckInnerHeight: 300 };
    case 2:
      return { truckWidth: 975, truckInnerWidth: 715, truckInnerHeight: 280 };
    case 3:
      return { truckWidth: 1272, truckInnerWidth: 985, truckInnerHeight: 270 };
    default:
      return { truckWidth: 0, truckInnerWidth: 0, truckInnerHeight: 0 };
  }
};

const getTruckLeft = (left: string, width: number, scale: number): string => {
  switch (left) {
    case "0%":
      return `calc(0% - ${width * scale}px)`;
    case "50%":
      return `calc(50% - ${(width * scale) / 2}px)`;
    case "100%":
      return "100%";
    default:
      return null;
  }
};

interface Props {
  itemLeft: number;
}

const GamePage = (props: Props) => {
  const dispatch = useDispatch();
  const [imgLoaded, addImgLoaded] = useImgLoaded();
  const { itemLeft } = props;

  const { grade, unit, webUrl } = useSelector((state: State) => state.shared);
  const {
    place,
    truck,
    truckType,
    display,
    answer,
    answered,
    response,
    truckActive,
    truckTransition,
    truckLeft,
    fileUnit,
  } = useSelector((state: State) => state.highFrequencyWord);

  const scale = 0.9;
  const { truckWidth, truckInnerWidth, truckInnerHeight } = getTruckSpec(
    truckType
  );
  const truckWrapperLeft = getTruckLeft(truckLeft, truckWidth, scale);

  const playAnswerAudio = useAudio(
    `high-frequency-word/audios/P${grade}/Stage ${
      fileUnit || unit
    }/P${grade} HFW List_Stage ${fileUnit || unit}_${answer}.wav`
  );

  useEffect(() => {
    dispatch(highFrequencyWordActions.setNewRound());
  }, [dispatch]);

  return (
    <div style={{ display: imgLoaded ? "block" : "none" }}>
      <div
        className={styles.pageContainer}
        style={{ width: defaultWidth, height: defaultHeight }}
      >
        <div
          className={styles.scrollBg}
          style={{
            backgroundImage: `url(${srcPath(
              `high-frequency-word/bg/${place}.png`
            )})`,
          }}
        ></div>
        <img
          className={classnames("abs", "btn", "main-btn", styles.topRightBack)}
          style={{
            right: `${itemLeft > maxLeft ? maxLeft : itemLeft + 30}px`,
          }}
          src={srcPath("shared/back.png")}
          alt=""
          onLoad={addImgLoaded}
          onClick={() => backToWeb("small_book", webUrl, grade, unit)}
        />
        <div
          className={styles.truckContainer}
          style={{
            width: `calc(100% - ${itemLeft * 2}px)`,
            left: `${itemLeft}px`,
          }}
        >
          <div
            className={styles.truckWrapper}
            style={{
              width: `${truckWidth * scale}px`,
              left: truckWrapperLeft,
              transition: truckTransition
                ? `left ${TRUCK_SPEED}ms ${
                    truckLeft === "100%" ? "ease-in" : "ease-out"
                  }`
                : "none",
            }}
          >
            {truck && (
              <img
                className={styles.truck}
                src={srcPath(
                  `high-frequency-word/trucks/gif/NET_Game1_track_${truck}.gif`
                )}
                alt=""
              />
            )}
            <div
              className={styles.truckInnerContainer}
              style={{
                width: `${truckInnerWidth * scale}px`,
                height: `${truckInnerHeight * scale}px`,
              }}
            >
              <div className={styles.truckWordCard}>
                <WordCard
                  word={answered}
                  style={{
                    position: "absolute",
                    zIndex: 13,
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "375px",
                    height: "185px",
                  }}
                />
                {response && (
                  <img
                    className={styles.response}
                    src={srcPath(`shared/answer_${response}.png`)}
                    alt=""
                  />
                )}
              </div>
              <img
                className={classnames(
                  "abs",
                  "btn",
                  "main-btn",
                  styles.truckSpeaker
                )}
                style={{
                  opacity: truckActive ? 1 : 0,
                  cursor: truckActive ? "pointer" : "default",
                }}
                src={srcPath("shared/speaker.png")}
                alt=""
                onClick={() => (truckActive ? playAnswerAudio() : {})}
              />
            </div>
          </div>
        </div>
        <div
          className={styles.wordCardContainer}
          style={{
            width: `calc(100% - ${itemLeft * 2}px)`,
            left: `${itemLeft}px`,
          }}
        >
          {display.map((d, i) => (
            <WordCard
              key={i}
              word={d}
              index={i}
              style={{
                transform: `scale(${truckActive ? 1 : 0})`,
                transition: "transform 200ms",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default GamePage;
